/**
 * @file build-intent.ts
 * @description Builds the canonical typed `Intent` for a completed slash-command
 * flow (selections → Intent). Replaces the legacy `buildResult` in
 * `useGameSlashCommands.ts` (the older `IntentHint` plumbing was deleted in
 * Phase A, May 2026 — slash-command builders now emit a fully typed `Intent`
 * directly, no hint layer).
 *
 * The FE owns the wire shape now — there's no enricher conversion seam.
 * Selections + the originating `GameSlashCommand` either compose into a fully
 * typed `Intent` (FE → `enrich_intent` → handler) or, when there isn't enough
 * information to build a complete Intent (e.g. `/attack` with no target chosen,
 * `/vote` with no active decision id), return `intent: null` so the caller
 * routes the player text through the LLM agent (`player_text` path).
 *
 * See `docs/game-engine/TYPED_INTENTS.md` for the full design.
 */

import type {
  Ability,
  ActionSlot,
  AttackSpec,
  BestowCurseEffect,
  DamageType,
  Intent,
  IntentAttack,
  IntentBuy,
  IntentCastSpell,
  IntentClose,
  IntentDrop,
  IntentEmote,
  IntentEquip,
  IntentFontOfInspiration,
  IntentGive,
  IntentGiveCoins,
  IntentHelp,
  IntentInteract,
  IntentInvestigate,
  IntentMove,
  IntentMoveConcentrationMark,
  IntentObserve,
  IntentOpen,
  IntentPickUp,
  IntentProposeDecision,
  IntentRequestCheck,
  IntentSay,
  IntentSell,
  IntentShout,
  IntentStabilize,
  IntentStudy,
  IntentTalkTo,
  IntentTravel,
  IntentUnequip,
  IntentUseItem,
  IntentWhisper,
  RoleplayDecisionType,
  Skill,
  SpellTarget,
  UsableItem,
} from '@danda/shared';
import { PICKUP_ALL_SENTINEL } from '@danda/shared';
import type { SlashCommandDefinition } from '@/components/panels/chat-commands';
import type { GameSlashCommand, CompletionStepType } from '@/components/panels/game-commands';
import type { BuiltIntent } from './build-from-card';

/** Build the per-character `SpellTarget` wire entry. Mirrors Rust
 *  `SpellTarget { target_type: SpellTargetType::Character, character_id }`.
 *  Pre-fix the FE shipped `{ target_id }` which Rust deserialised with
 *  `character_id: None` — every `/cast Fireball at Goblin` lost its target. */
function characterSpellTarget(targetId: string): SpellTarget {
  return { target_type: 'character', character_id: targetId };
}

/** SRD reaction spells — when the FE-direct path ships these via /cast, we
 *  tag the wire envelope with `action_slot: 'reaction'` so the backend
 *  routes them through the reaction-subscription channel rather than burning
 *  the action slot. The backend's secondary defense-in-depth check (matching
 *  on spell name) covers any client that forgets the slot. */
/** Reaction-only spells per SRD 5.2 — auto-fire via the engine's reaction
 *  subscription system on their natural trigger (taken hit, spell-cast
 *  in range, falling, etc.). The proactive cast menu hides them so they
 *  can't be slash-cast (would just land in the engine's rejection
 *  softener since there's no trigger context). Used for both the
 *  cast-menu filter in `game-commands.ts` and the action-slot override
 *  here so the wire shape stays consistent if some client ever forgets
 *  to filter. Keep lower-cased + normalized (both 'feather fall' and
 *  'featherfall' map). */
export const REACTION_SPELLS = new Set<string>([
  'shield',
  'counterspell',
  'hellish rebuke',
  'feather fall',
  'featherfall',
]);

function slotForCastSpell(spellName: string | undefined): ActionSlot {
  if (!spellName) return 'action';
  return REACTION_SPELLS.has(spellName.trim().toLowerCase()) ? 'reaction' : 'action';
}

export interface StepSelection {
  stepType: CompletionStepType;
  selectedItem: SlashCommandDefinition;
  /** When the step was a multi-select picker (e.g. /cast Bless target step
   *  with SRD's 3-target cap), this carries every item the user checked, in
   *  pick order. `selectedItem` is set to the first item for back-compat with
   *  consumers that only read one. Empty / undefined for single-select steps. */
  selectedItems?: SlashCommandDefinition[];
}

interface SelectionAccumulator {
  /** Spell name picked (from `spells` or `item_spells` step). */
  spellName?: string;
  /** Spell level (cantrip = 0). */
  spellLevel?: number;
  /** Primary target id (from any target picker step). */
  targetId?: string;
  /** Multi-target ids (currently unused by selection flow but kept for parity). */
  targetIds?: string[];
  /** Picked inventory/scene item id. */
  itemId?: string;
  /** Picked inventory/scene item display name. */
  itemName?: string;
  /** Bulk-pickup sentinel — set when scene_items step picked the "All" entry. */
  pickupAll?: boolean;
  /** Picked scene-interactable id (for /open, /close, /investigate, /break). */
  objectId?: string;
  /** Object display name (for break-action verb). */
  objectName?: string;
  /** Picked ability name (item_abilities step on magic items). */
  abilityName?: string;
  /** Pool spend amount (Lay on Hands). */
  poolSpend?: number;
  /** Travel destination id with `scene:`/`path:` prefix. */
  exitId?: string;
  /** Spell slot level picked (Bard L5+ Font of Inspiration). */
  slotLevel?: number;
  /** Damage type picked for Sorcerer Transmuted Spell metamagic. SRD 5.2:
   *  one of acid, cold, fire, lightning, poison, thunder. Threaded into
   *  the merged `IntentCastSpell.transmuted_damage_type`. */
  transmutedDamageType?: DamageType;
  /** Flag set by the `slot_level_choice` step when the picked level
   *  is an innate / Magic Initiate free cast (`meta.freeCast === true`).
   *  `buildPlainIntent` forwards it to `IntentCastSpell.free_cast` so the
   *  backend skips slot consumption + spell-list checks. */
  freeCast?: boolean;
  /** Second-target character id from the `twinned_target` step (Sorcerer
   *  Twinned Spell metamagic). `mergeCardIntent` forwards it to
   *  `IntentCastSpell.twinned_target`. */
  twinnedTarget?: string;
  /** Excluded ally char ids from the `careful_exclude` multi-select step
   *  (Sorcerer Careful Spell metamagic). `mergeCardIntent` forwards them
   *  to `IntentCastSpell.careful_exclude_targets`. */
  carefulExcludeTargetIds?: string[];
  /** Bestow Curse caster's effect pick from the `bestow_curse_effect` step.
   *  Threaded into `IntentCastSpell.bestow_curse_effect`. When the user
   *  picked `AbilityDisadvantage`, the chosen ability rides on the variant
   *  payload. Omitted casts fall back to `CasterDealsExtraNecrotic` server-
   *  side (with a warning) so a partial selection still resolves. */
  bestowCurseEffect?: BestowCurseEffect;
}

/** Pull canonical fields out of all selections, regardless of step ordering. */
function accumulateSelections(selections: StepSelection[]): SelectionAccumulator {
  const acc: SelectionAccumulator = {};
  for (const sel of selections) {
    const name = sel.selectedItem.name;
    const sourceId = sel.selectedItem.sourceId;
    const meta = sel.selectedItem.meta;

    switch (sel.stepType) {
      case 'spells':
        acc.spellName = name;
        acc.spellLevel = (meta?.spellLevel as number | undefined) ?? acc.spellLevel;
        break;
      case 'item_spells':
        acc.spellName = name;
        break;
      case 'enemies':
      case 'allies':
      case 'allies_or_self':
      case 'dying_allies':
      case 'dead_allies':
      case 'all_targets':
      case 'friendly_targets':
      case 'scene_chars':
      case 'scene_npcs':
      case 'party_members':
        if (sourceId) acc.targetId = sourceId;
        // Multi-select picker (Bless target step etc.) — surface the full
        // array of checked target ids. The cast_spell builder reads
        // `acc.targetIds` first and falls back to single-target
        // `acc.targetId` for the regular flow.
        if (sel.selectedItems && sel.selectedItems.length > 0) {
          acc.targetIds = sel.selectedItems
            .map((it) => it.sourceId)
            .filter((id): id is string => !!id);
        }
        break;
      case 'inventory_items':
      case 'usable_items':
      case 'equippable_items': {
        const cleanName = (meta?.itemName as string | undefined) ?? name;
        acc.itemName = cleanName;
        if (sourceId) acc.itemId = sourceId;
        break;
      }
      case 'scene_items': {
        const sceneMetaName = meta?.itemName as string | undefined;
        if (sceneMetaName === PICKUP_ALL_SENTINEL) {
          acc.pickupAll = true;
          acc.itemName = PICKUP_ALL_SENTINEL;
          acc.itemId = '';
        } else {
          acc.itemName = name;
          if (sourceId) acc.itemId = sourceId;
        }
        break;
      }
      case 'scene_objects':
      case 'openable_objects':
      case 'closable_objects':
      case 'breakable_objects':
        if (sourceId) acc.objectId = sourceId;
        acc.objectName = name;
        break;
      case 'item_abilities':
        acc.abilityName = name;
        break;
      case 'pool_spend': {
        const metaSpend = meta?.poolSpend as number | undefined;
        if (metaSpend !== undefined) {
          acc.poolSpend = metaSpend;
        } else {
          const parsed = Number(sourceId);
          acc.poolSpend = Number.isFinite(parsed) ? parsed : 1;
        }
        break;
      }
      case 'spell_slot_levels': {
        const metaLevel = meta?.slotLevel as number | undefined;
        if (metaLevel !== undefined) {
          acc.slotLevel = metaLevel;
        } else {
          const parsed = Number(sourceId);
          if (Number.isFinite(parsed)) acc.slotLevel = parsed;
        }
        break;
      }
      case 'twinned_target': {
        if (sourceId) acc.twinnedTarget = sourceId;
        break;
      }
      case 'careful_exclude': {
        // Multi-select picker — `selectedItems` carries every checked
        // ally. Fall back to a single-target shape if only the primary
        // `selectedItem` is present (shouldn't happen since the step is
        // always multi-select, but defensive).
        const ids = sel.selectedItems?.length
          ? sel.selectedItems.map((it) => it.sourceId).filter((id): id is string => !!id)
          : sourceId
            ? [sourceId]
            : [];
        if (ids.length > 0) acc.carefulExcludeTargetIds = ids;
        break;
      }
      case 'slot_level_choice': {
        // Upcast picker — overrides the spell's base level (set by the
        // `spells` step earlier in the selection chain) with the picked
        // slot. Free-cast picks carry `meta.freeCast: true` so the
        // accumulator can flip `acc.freeCast` for the cast-from-source
        // path; the slot_level stays at the spell's base level for
        // free casts (no slot consumed at any tier).
        const metaLevel = meta?.slotLevel as number | undefined;
        if (metaLevel !== undefined) acc.spellLevel = metaLevel;
        if (meta?.freeCast === true) acc.freeCast = true;
        break;
      }
      case 'transmuted_damage_type': {
        // Sorcerer Transmuted Spell metamagic. The builder stores the
        // canonical wire value (acid/cold/fire/...) in meta and as
        // sourceId; either is acceptable. Fall back to sourceId so a
        // future trimmed-meta builder still threads the pick through.
        const metaType = meta?.transmutedDamageType as DamageType | undefined;
        if (metaType) {
          acc.transmutedDamageType = metaType;
        } else if (sourceId) {
          acc.transmutedDamageType = sourceId as DamageType;
        }
        break;
      }
      case 'bestow_curse_effect': {
        // 4-effect picker (SRD 5.2 Bestow Curse). The first step picks one of
        // the four variants; for `AbilityDisadvantage` the chain continues to
        // an `ability_picker` sub-step that fills in which ability — the
        // accumulator merges them into a single `BestowCurseEffect`. The
        // wire shape mirrors the Rust enum:
        //   `"AttacksVsCasterDisadvantage" | "ForceDodgeOnFailedWis" |
        //    "CasterDealsExtraNecrotic" | { AbilityDisadvantage: { ability } }`
        const variant = (meta?.bestowCurseVariant as string | undefined) ?? sourceId;
        if (variant === 'AttacksVsCasterDisadvantage') {
          acc.bestowCurseEffect = 'AttacksVsCasterDisadvantage';
        } else if (variant === 'ForceDodgeOnFailedWis') {
          acc.bestowCurseEffect = 'ForceDodgeOnFailedWis';
        } else if (variant === 'CasterDealsExtraNecrotic') {
          acc.bestowCurseEffect = 'CasterDealsExtraNecrotic';
        } else if (variant === 'AbilityDisadvantage') {
          // Ability sub-pick lands on the next selection (case `ability_picker`).
          // Sentinel marks "waiting for ability" — the ability_picker case
          // upgrades the placeholder to the typed variant.
          acc.bestowCurseEffect = {
            AbilityDisadvantage: { ability: 'strength' as Ability },
          };
        }
        break;
      }
      case 'ability_picker': {
        // Generic ability sub-picker — currently only consumed by the
        // Bestow Curse 4-effect flow's `AbilityDisadvantage` branch above.
        // The picked ability source-id maps directly to the canonical
        // lower-case ability key (strength, dexterity, …) the backend's
        // `Ability` enum deserializes from.
        if (
          typeof acc.bestowCurseEffect === 'object' &&
          'AbilityDisadvantage' in acc.bestowCurseEffect &&
          sourceId
        ) {
          acc.bestowCurseEffect = {
            AbilityDisadvantage: { ability: sourceId as Ability },
          };
        }
        break;
      }
      case 'travel_destinations':
        if (sourceId) acc.exitId = sourceId;
        break;
    }
  }
  return acc;
}

function escapeRegExp(literal: string): string {
  return literal.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Substitute a placeholder, dropping the template's article when the selected
 * name carries its own. Templates hard-code an article before the placeholder
 * (`'Break the {object}'`), and authored content routinely names things with
 * one baked in — The Bleeding Gate ships "The Deep Dark Pact-Altar", which
 * rendered as "Break the The Deep Dark Pact-Altar" in the chip and went out
 * over the wire that way. The template's article is what gives, so the
 * authored name survives verbatim for both display and LLM name-matching.
 *
 * Lives here rather than beside the slash-command menu because BOTH text
 * builders need it and only this one is on the path that ships. A single-step
 * command (`/break` is one: `completionSteps: ['breakable_objects']`) never
 * leaves step 0, so the menu's `buildPartialText` never runs for it and the
 * submitted text comes from `fillTemplate` below. Keeping a second copy over
 * there is what made the first attempt at this fix dead code.
 */
export function substitutePlaceholder(text: string, placeholder: string, name: string): string {
  const leadingArticle = /^(the|a|an)\s+/i.test(name);
  if (leadingArticle) {
    const withArticle = new RegExp(`\\b(the|a|an)\\s+${escapeRegExp(placeholder)}`, 'i');
    if (withArticle.test(text)) {
      return text.replace(withArticle, name);
    }
  }
  return text.replace(placeholder, name);
}

/** Replace `{...}` placeholders in a template using selection data. */
function fillTemplate(template: string, selections: StepSelection[]): string {
  let text = template;
  for (const sel of selections) {
    const name = sel.selectedItem.name;
    const meta = sel.selectedItem.meta;

    switch (sel.stepType) {
      case 'spells':
      case 'item_spells':
        text = substitutePlaceholder(text, '{spell}', name);
        break;
      case 'enemies':
      case 'allies':
      case 'allies_or_self':
      case 'dying_allies':
      case 'dead_allies':
      case 'all_targets':
      case 'friendly_targets':
      case 'scene_chars':
      case 'scene_npcs':
      case 'party_members':
        text = substitutePlaceholder(text, '{target}', name);
        break;
      case 'inventory_items':
      case 'usable_items':
      case 'equippable_items': {
        const cleanName = (meta?.itemName as string | undefined) ?? name;
        text = text.replace('{item}', cleanName);
        break;
      }
      case 'scene_items': {
        const sceneMetaName = meta?.itemName as string | undefined;
        text = text.replace('{item}', sceneMetaName === PICKUP_ALL_SENTINEL ? 'everything' : name);
        break;
      }
      case 'scene_objects':
      case 'openable_objects':
      case 'closable_objects':
      case 'breakable_objects':
        text = substitutePlaceholder(text, '{object}', name);
        break;
      case 'item_abilities':
        text = substitutePlaceholder(text, '{ability}', name);
        break;
      case 'pool_spend': {
        const amount = (meta?.poolSpend as number | undefined) ?? 1;
        text = text.replace('{amount}', `${amount}`);
        break;
      }
      case 'travel_destinations':
        text = substitutePlaceholder(text, '{destination}', name);
        break;
      case 'spell_slot_levels': {
        const level =
          (meta?.slotLevel as number | undefined) ?? (Number(sel.selectedItem.sourceId) || 0);
        text = text.replace('{level}', `${level}`);
        break;
      }
      case 'slot_level_choice':
      case 'twinned_target':
      case 'careful_exclude':
      case 'concentration_confirm':
        // None of these contribute a placeholder to the canonical
        // bubble text (e.g. /cast still reads "Cast Cure Wounds at
        // Bram"). The picked values live on the typed Intent itself,
        // or — for concentration_confirm — drive a side-effect at cast
        // time without showing in the bubble.
        break;
      case 'transmuted_damage_type': {
        // The builder emits `name` as "🔥 Fire" (emoji + label); strip the
        // emoji prefix when filling the textTemplate so the rendered
        // command reads naturally ("Cast Burning Hands with Transmuted
        // Spell (Cold) on goblin"), not "(❄️ Cold)".
        const label =
          (meta?.transmutedDamageType as string | undefined) ??
          name.replace(/^[^\sA-Za-z]+\s*/, '');
        const display = label.charAt(0).toUpperCase() + label.slice(1);
        text = text.replace('{damageType}', display);
        break;
      }
    }
  }

  // Strip dangling unfilled placeholders + the connector word before them.
  // Fires when a multi-step command finalizes with fewer selections than
  // placeholders (e.g. `/use Lantern` skips the friendly_targets step,
  // `/use Potion of Healing` typed raw bypasses target picker).
  const unfilled = text.match(/\{[^}]+\}/);
  if (unfilled && unfilled.index !== undefined) {
    return text
      .slice(0, unfilled.index)
      .replace(/\s+(on|at|to|with)\s*$/i, '')
      .trimEnd();
  }
  return text;
}

/** Build the canonical text for a slash command + selections. */
function buildText(command: GameSlashCommand, selections: StepSelection[]): string {
  if (command.textTemplate) return fillTemplate(command.textTemplate, selections);
  return command.fillText ?? '';
}

/** Merge selection-derived fields onto a card-derived Intent. The card-built
 *  Intent carries the structural facts (spell name for `cast_spell_from_source`,
 *  metamagic option, flurry flag, ability name, item id); the selection
 *  fills in the runtime variables (target ids, picked spell for metamagic,
 *  pool-spend amount). Selection wins for overlapping fields like target_id;
 *  card stays authoritative for everything it set. */
function mergeCardIntent(cardIntent: Intent, acc: SelectionAccumulator): Intent {
  switch (cardIntent.intent_type) {
    case 'attack': {
      // Card-built attack normally has a populated `attacks` array (one entry
      // per target picked at card-construction time). When the Flurry of
      // Blows card is selected without a target picked at click time, the
      // stub Intent has empty `attacks` and the target arrives via the
      // selection accumulator — fill in. The `flurry_of_blows: true` flag
      // routes to an unarmed strike variant (Monk's bonus action); any
      // other empty-attacks attack stub falls through to a weapon variant
      // (engine derives the actual weapon from the equipped main hand
      // when `weapon_name` is empty).
      //
      // Reckless Attack is NOT in this branch — its card click emits a
      // Wait intent with `turn_flags.reckless_attack: true`, see
      // `build-from-card.ts` Reckless arm.
      if (cardIntent.attacks.length === 0 && acc.targetId) {
        const variant = cardIntent.flurry_of_blows
          ? ({
              kind: 'unarmed_strike' as const,
              option_type: 'damage' as const,
              target_id: acc.targetId,
            } as const)
          : ({
              kind: 'weapon' as const,
              weapon_name: '',
              target_id: acc.targetId,
            } as const);
        return {
          ...cardIntent,
          attacks: [{ variant }],
        };
      }
      return cardIntent;
    }
    case 'cast_spell': {
      const merged: IntentCastSpell = { ...cardIntent };
      // Metamagic card is a stub at construction — the selection step picks
      // the actual spell and target, fill those in here.
      if (acc.spellName && (!merged.spell_name || merged.spell_name === '')) {
        merged.spell_name = acc.spellName;
      }
      // `slot_level: 0` is the canonical stub value for cards that don't know
      // the cast level at construction time (cast_spell_from_source,
      // spell_cast_with_metamagic). We want the spell-picker selection to fill
      // it in. Using `=== undefined` instead of falsy `!merged.slot_level` so
      // a deliberately-set 0 (cantrip cast via a future cantrip card) isn't
      // accidentally overwritten by undefined acc.spellLevel.
      if (
        acc.spellLevel !== undefined &&
        (merged.slot_level === undefined || merged.slot_level === 0)
      ) {
        merged.slot_level = acc.spellLevel;
      }
      // Multi-target buff spells (Bless and friends) routed via a metamagic
      // card: the target picker ran in multi-select mode and populated
      // `acc.targetIds`. Build one `SpellTarget` per picked id so every
      // selected creature gets the buff — `acc.targetId` was a single-target
      // bug that silently dropped extra picks before the `targetIds` branch.
      const pickedTargets: SpellTarget[] = acc.targetIds?.length
        ? acc.targetIds.map((id) => characterSpellTarget(id))
        : acc.targetId
          ? [characterSpellTarget(acc.targetId)]
          : [];
      if (
        pickedTargets.length > 0 &&
        (merged.targets.length === 0 || merged.targets.every((t) => !t.character_id))
      ) {
        merged.targets = pickedTargets;
      }
      // Transmuted Spell metamagic — replace the card stub's Fire default
      // with the picker's pick. Only fires when the player actually walked
      // through the `transmuted_damage_type` step; absent that, the card
      // stub's `transmuted_damage_type: 'fire'` stays put for the legacy
      // click-fast path.
      if (acc.transmutedDamageType) {
        merged.transmuted_damage_type = acc.transmutedDamageType;
      }
      // Twinned Spell — second target captured by the `twinned_target`
      // step. Backend validator rejects this field on non-twinnable
      // spells, so the FE never gets a chance to ship it unless the
      // picker successfully ran (the empty-state bubble fires otherwise).
      if (acc.twinnedTarget) {
        merged.twinned_target = acc.twinnedTarget;
      }
      // Careful Spell — exclude-list captured by the `careful_exclude`
      // multi-select step. Cap is enforced by the engine validator.
      if (acc.carefulExcludeTargetIds && acc.carefulExcludeTargetIds.length > 0) {
        merged.careful_exclude_targets = acc.carefulExcludeTargetIds;
      }
      return merged;
    }
    case 'use_ability': {
      const merged = { ...cardIntent };
      if (acc.targetId && (merged.target_ids.length === 0 || merged.target_ids.every((t) => !t))) {
        merged.target_ids = [acc.targetId];
      }
      if (acc.poolSpend !== undefined && merged.pool_spend === undefined) {
        merged.pool_spend = acc.poolSpend;
      }
      return merged;
    }
    case 'use_item': {
      const merged: IntentUseItem = { ...cardIntent };
      if (acc.targetId && (!merged.target_ids || merged.target_ids.length === 0)) {
        merged.target_ids = [acc.targetId];
      }
      return merged;
    }
    case 'help':
    case 'study':
    case 'stabilize': {
      // These first-class intents need a target_id — if the card didn't fill
      // it (no target picked at click time) and the selection did, swap in.
      if (!cardIntent.target_id && acc.targetId) {
        return { ...cardIntent, target_id: acc.targetId };
      }
      return cardIntent;
    }
    default:
      return cardIntent;
  }
}

/** Build a non-card Intent for the static slash commands (e.g. /attack, /cast,
 *  /talk, /pickup, /travel, etc.) using the command's `intentType` discriminator
 *  and the accumulated selections. Returns `null` when there isn't enough
 *  information to build a complete Intent — caller falls back to `player_text`.
 *
 *  Returns `{ intent, action_slot }` so the wire envelope can be filled
 *  directly. Most slash commands map to `'action'`; `/cast Shield` and
 *  friends map to `'reaction'`. */
function buildPlainIntent(
  command: GameSlashCommand,
  acc: SelectionAccumulator,
  selections: StepSelection[]
): BuiltIntent | null {
  const intentType = command.intentType;
  const cmdMeta = command.meta;
  const wrap = (intent: Intent, slot: ActionSlot = 'action'): BuiltIntent => ({
    intent,
    action_slot: slot,
  });

  switch (intentType) {
    case 'attack': {
      // Plain `/attack <target>` or weapon shortcut → single weapon attack.
      // Need a target_id; weapon name comes from `command.meta.weaponName` if
      // set (triggerWeaponAttackShortcut), otherwise unspecified — enricher
      // resolves the equipped main weapon.
      if (!acc.targetId) return null;
      const weaponName = cmdMeta?.weaponName as string | undefined;
      const attack: AttackSpec = {
        variant: {
          kind: 'weapon',
          weapon_name: weaponName ?? '',
          target_id: acc.targetId,
        },
      };
      const intent: IntentAttack = {
        intent_type: 'attack',
        attacks: [attack],
      };
      return wrap(intent);
    }

    case 'cast_spell': {
      // Plain `/cast Spell at Target` or dynamic spell command + target picker.
      const spellName = acc.spellName ?? (cmdMeta?.spellName as string | undefined);
      if (!spellName) return null;
      const slotLevel =
        acc.spellLevel ??
        (cmdMeta?.spellLevel as number | undefined) ??
        // Cantrips are level 0; `isCantrip` meta lets us distinguish from
        // "level unknown" so the wire format never sends `slot_level: 1` for a
        // cantrip.
        (cmdMeta?.isCantrip ? 0 : undefined);
      if (slotLevel === undefined) return null;
      // Multi-target buff spells (Bless and friends): the picker's target
      // step ran in multi-select mode and produced `acc.targetIds`. Build
      // one SpellTarget per picked id so the engine's `resolve_buff_spell`
      // loop applies the buff to every checked creature. `acc.targetId`
      // remains the single-target fallback for everything else.
      const targets: SpellTarget[] = acc.targetIds?.length
        ? acc.targetIds.map((id) => characterSpellTarget(id))
        : acc.targetId
          ? [characterSpellTarget(acc.targetId)]
          : [];
      const intent: IntentCastSpell = {
        intent_type: 'cast_spell',
        spell_name: spellName,
        slot_level: slotLevel,
        targets,
      };
      // Item-spell from a magic item: signal `free_cast: true` because the
      // backend treats item-spells as already-paid (charge consumption is
      // separate from slot consumption).
      if ((cmdMeta?.itemName as string | undefined) && acc.spellName) {
        intent.free_cast = true;
      }
      // Upcast picker for an innate / Magic Initiate spell flagged
      // `meta.freeCast === true` on the slot row — forward to the
      // backend free-cast pipeline.
      if (acc.freeCast === true) {
        intent.free_cast = true;
      }
      // Bestow Curse 4-effect picker — only meaningful when the spell IS
      // Bestow Curse, but the accumulator carries it regardless of spell
      // and the backend only reads it on the `SpellEffect::BestowCurse`
      // dispatch arm.
      if (acc.bestowCurseEffect !== undefined) {
        intent.bestow_curse_effect = acc.bestowCurseEffect;
      }
      return wrap(intent, slotForCastSpell(spellName));
    }

    case 'use_ability': {
      const abilityName = acc.abilityName ?? (cmdMeta?.abilityName as string | undefined);
      if (!abilityName) return null;
      // Pool-spend self-only carve-out (#275, 2026-05-10): when the synthetic
      // shortcut command is built with `meta.selfOnlyPool: true` (Lay on Hands
      // legacy fallback path — fires when action_cards is empty post-restore
      // and the click-path falls back to triggerAbilityShortcut), the
      // `pool_spend` step contributes the picked HP amount via `acc.poolSpend`.
      // Build a UseAbility intent with `pool_spend` set + self-targeting empty
      // `target_ids` (engine resolves self when no explicit target). Without
      // this, the picked HP amount is dropped and the engine defaults to 1 HP.
      const isSelfOnlyPool = (cmdMeta?.selfOnlyPool as boolean | undefined) === true;
      if (isSelfOnlyPool && acc.poolSpend !== undefined) {
        const intent: Intent = {
          intent_type: 'use_ability',
          ability_name: abilityName,
          target_ids: [],
          pool_spend: acc.poolSpend,
        };
        return wrap(intent);
      }
      const intent: Intent = {
        intent_type: 'use_ability',
        ability_name: abilityName,
        target_ids: acc.targetId ? [acc.targetId] : [],
      };
      // Use-ability slash commands default to action; class abilities that
      // are bonus-action only (Second Wind, Cunning Action, …) are handled
      // via the action-card path which carries `availability.action_cost`.
      return wrap(intent);
    }

    case 'use_item': {
      const itemName = acc.itemName ?? (cmdMeta?.itemName as string | undefined);
      const itemId = acc.itemId ?? (cmdMeta?.itemId as string | undefined);
      if (!itemName) return null;
      const item: UsableItem = {
        item_type: 'magic_item',
        item_name: itemName,
        charges_used: 1,
        ...(itemId ? { item_id: itemId } : {}),
      };
      const intent: IntentUseItem = {
        intent_type: 'use_item',
        item,
        target_ids: acc.targetId ? [acc.targetId] : undefined,
      };
      // Item-spell (Wand of Fireball etc.) — propagate `spell_choice`. The
      // selection step contributes via `acc.spellName`; the synthetic-command
      // path (single-spell shortcut from `triggerItemShortcut`) carries the
      // spell name on `meta.spellName` instead, so fall back to it.
      const spellChoice = acc.spellName ?? (cmdMeta?.spellName as string | undefined);
      if (spellChoice && item.item_type === 'magic_item') item.spell_choice = spellChoice;
      // Item-ability (Cloak of Displacement Activate) — propagate
      // `ability_choice`. Same dual source: selection step or synthetic
      // command meta.
      const abilityChoice = acc.abilityName ?? (cmdMeta?.abilityName as string | undefined);
      if (abilityChoice && item.item_type === 'magic_item') item.ability_choice = abilityChoice;

      // Optimistic charge mirror: when this is a charged magic item (spell or
      // ability cast through a Wand/Staff/Rod), surface item_id + count on
      // BuiltIntent so ChatPanel can decrement permanent.magic_item_mechanics
      // + char.equipment immediately on click. Without this, action-panel
      // item clicks display stale charges until the round result lands.
      // Skipped for plain potions/gear (no spell/ability choice) — those
      // emit items_lost, which is handled in the WS path, not item charges.
      let itemCharge: { item_id: string; count: number } | undefined;
      if (itemId && (spellChoice || abilityChoice)) {
        let count: number | undefined;
        if (spellChoice) {
          const single = cmdMeta?.spellCharges as number | undefined;
          if (typeof single === 'number' && single > 0) {
            count = single;
          } else {
            const spells = cmdMeta?.itemSpells as
              | Array<{ spellName?: string; charges?: number }>
              | undefined;
            const match = spells?.find((s) => s.spellName === spellChoice);
            if (typeof match?.charges === 'number' && match.charges > 0) count = match.charges;
          }
        } else if (abilityChoice) {
          const single = cmdMeta?.abilityCharges as number | undefined;
          if (typeof single === 'number' && single > 0) {
            count = single;
          } else {
            const abilities = cmdMeta?.itemAbilities as
              | Array<{ name?: string; charges?: number }>
              | undefined;
            const match = abilities?.find((a) => a.name === abilityChoice);
            if (typeof match?.charges === 'number' && match.charges > 0) count = match.charges;
          }
        }
        if (count && count > 0) itemCharge = { item_id: itemId, count };
      }
      return { ...wrap(intent), itemCharge };
    }

    case 'dodge':
      return wrap({ intent_type: 'dodge' });
    case 'disengage':
      return wrap({ intent_type: 'disengage' });
    case 'dash':
      return wrap({ intent_type: 'dash' });
    case 'hide':
      return wrap({ intent_type: 'hide' });
    case 'search':
      return wrap({ intent_type: 'search' });
    case 'flee':
      return wrap({ intent_type: 'flee' });
    case 'drop_prone':
      return wrap({ intent_type: 'drop_prone' }, 'free');
    case 'stand_up':
      return wrap({ intent_type: 'stand_up' }, 'free');
    case 'steady_aim':
      return wrap({ intent_type: 'steady_aim' }, 'bonus_action');
    case 'end_concentration':
      return wrap({ intent_type: 'end_concentration' }, 'free');
    case 'cancel_proposal':
      return wrap({ intent_type: 'cancel_proposal' }, 'free');
    case 'wait':
      return wrap({ intent_type: 'wait' }, 'free');

    case 'death_save':
      return wrap({ intent_type: 'death_save' }, 'free');

    case 'stabilize': {
      if (!acc.targetId) return null;
      const intent: IntentStabilize = { intent_type: 'stabilize', target_id: acc.targetId };
      return wrap(intent);
    }

    case 'wake_up': {
      if (!acc.targetId) return null;
      // Free action per `WakeUpHandler` (stance.rs) and COMBAT_SYSTEM.md —
      // shaking/shouting a downed ally awake doesn't burn the Action slot,
      // so a player who wakes someone then attacks in the same round must
      // have both intents merge onto one CharIntent via `slots_disjoint`.
      return wrap({ intent_type: 'wake_up', target_id: acc.targetId }, 'free');
    }

    case 'escape_grapple': {
      // Slash flow doesn't surface a grappler picker. The deterministic
      // enricher fills `grappler_id` from the active Grappled condition's
      // `source_id` when we ship empty.
      return wrap({ intent_type: 'escape_grapple', grappler_id: acc.targetId ?? '' });
    }

    case 'help': {
      if (!acc.targetId) return null;
      const intent: IntentHelp = { intent_type: 'help', target_id: acc.targetId };
      return wrap(intent);
    }

    case 'study': {
      if (!acc.targetId) return null;
      const intent: IntentStudy = { intent_type: 'study', target_id: acc.targetId };
      return wrap(intent);
    }

    case 'pick_up': {
      // /pickup Item or /pickup all (sentinel)
      if (acc.pickupAll) {
        const intent: IntentPickUp = {
          intent_type: 'pick_up',
          item_id: '',
          item_name: PICKUP_ALL_SENTINEL,
        };
        return wrap(intent);
      }
      if (!acc.itemId || !acc.itemName) return null;
      const intent: IntentPickUp = {
        intent_type: 'pick_up',
        item_id: acc.itemId,
        item_name: acc.itemName,
      };
      return wrap(intent);
    }

    case 'drop': {
      if (!acc.itemId || !acc.itemName) return null;
      const intent: IntentDrop = {
        intent_type: 'drop',
        item_id: acc.itemId,
        item_name: acc.itemName,
      };
      return wrap(intent, 'free');
    }

    case 'open': {
      if (!acc.objectId) return null;
      const intent: IntentOpen = { intent_type: 'open', object_id: acc.objectId };
      return wrap(intent, 'free');
    }

    case 'close': {
      if (!acc.objectId) return null;
      const intent: IntentClose = { intent_type: 'close', object_id: acc.objectId };
      return wrap(intent, 'free');
    }

    case 'interact': {
      if (!acc.objectId) return null;
      // /break uses intentType: 'interact' with action: 'break' — pick up the
      // verb from the command's textTemplate or treat it as `break` when the
      // step was breakable_objects. Default to "interact" otherwise.
      const isBreak = selections.some((s) => s.stepType === 'breakable_objects');
      const intent: IntentInteract = {
        intent_type: 'interact',
        object_id: acc.objectId,
        action: isBreak ? 'break' : 'interact',
      };
      return wrap(intent);
    }

    case 'observe': {
      if (!acc.targetId && !acc.objectName) return null;
      const intent: IntentObserve = {
        intent_type: 'observe',
        target: acc.targetId ?? acc.objectName ?? '',
      };
      return wrap(intent, 'free');
    }

    case 'investigate': {
      if (!acc.objectId && !acc.objectName) return null;
      const intent: IntentInvestigate = {
        intent_type: 'investigate',
        target: acc.objectId ?? acc.objectName ?? '',
      };
      return wrap(intent);
    }

    case 'move': {
      // /move <destination> — destination comes from free text; without typed
      // destination we can't build the Intent.
      return null;
    }

    case 'travel': {
      if (!acc.exitId) return null;
      const intent: IntentTravel = { intent_type: 'travel', path_id: acc.exitId };
      return wrap(intent);
    }

    case 'request_check': {
      // No slash command currently uses this; fall back.
      return null;
    }

    case 'propose_decision': {
      const decisionType = command.decisionType as RoleplayDecisionType | undefined;
      if (!decisionType) return null;
      const intent: IntentProposeDecision = {
        intent_type: 'propose_decision',
        decision_type: decisionType,
        target_id: acc.targetId,
      };
      return wrap(intent, 'free');
    }

    case 'vote': {
      // The slash flow doesn't carry the active decision id; the backend
      // handler must resolve it from session state. We can't construct a
      // complete typed Intent here, so fall back to player_text.
      return null;
    }

    case 'equip': {
      if (!acc.itemId) return null;
      const intent: IntentEquip = { intent_type: 'equip', item_id: acc.itemId };
      return wrap(intent);
    }

    case 'unequip': {
      // Unequip needs a slot — slash flow doesn't pick one. Fall through.
      return null;
    }

    case 'give': {
      if (!acc.itemId || !acc.targetId) return null;
      const intent: IntentGive = {
        intent_type: 'give',
        item_id: acc.itemId,
        target_id: acc.targetId,
        // Always include item_name when the picker had it (it always does).
        // Mirrors /drop and /pick_up; protects against stale `item_id` in
        // the Rust enricher (it falls back to inventory name-match).
        ...(acc.itemName ? { item_name: acc.itemName } : {}),
      };
      return wrap(intent, 'free');
    }

    case 'give_coins': {
      // The amount lives in the user-typed body — no typed Intent without it.
      return null;
    }

    case 'buy': {
      if (!acc.itemId || !acc.targetId) return null;
      const intent: IntentBuy = {
        intent_type: 'buy',
        item_id: acc.itemId,
        from_npc_id: acc.targetId,
      };
      return wrap(intent);
    }

    case 'sell': {
      if (!acc.itemId || !acc.targetId) return null;
      const intent: IntentSell = {
        intent_type: 'sell',
        item_id: acc.itemId,
        to_npc_id: acc.targetId,
      };
      return wrap(intent);
    }

    case 'say':
    case 'shout':
    case 'emote':
    case 'talk_to':
    case 'whisper': {
      // All communication intents need a free-text body. The user-supplied
      // message lives outside the menu state — caller routes to player_text
      // when this returns null.
      return null;
    }

    case 'leave_party':
      return wrap({ intent_type: 'leave_party' }, 'free');
    case 'accept_party_invite':
      return wrap({ intent_type: 'accept_party_invite' }, 'free');
    case 'decline_party_invite':
      return wrap({ intent_type: 'decline_party_invite' }, 'free');

    case 'move_concentration_mark': {
      if (!acc.targetId) return null;
      const intent: IntentMoveConcentrationMark = {
        intent_type: 'move_concentration_mark',
        new_target_id: acc.targetId,
      };
      return wrap(intent, 'free');
    }

    case 'font_of_inspiration': {
      // Bard L5+ — expend a spell slot (no action) to regain a Bardic
      // Inspiration use. Free action per SRD 5.2.
      if (acc.slotLevel === undefined) return null;
      const intent: IntentFontOfInspiration = {
        intent_type: 'font_of_inspiration',
        slot_level: acc.slotLevel,
      };
      return wrap(intent, 'free');
    }

    case 'grant_bardic_inspiration':
    case 'influence':
      // Not wired into the slash menu today — fall back if encountered.
      return null;

    default:
      return null;
  }
}

/**
 * Build the canonical text + typed Intent envelope for a completed slash flow.
 *
 * Returns `{ text, built: null }` when the command is incomplete (missing
 * required field that wasn't picked through the menu) — the caller routes
 * the resulting text through `player_text` instead of `player_intent`.
 *
 * The `built` field is the wire envelope `{ intent, action_slot, turn_flags? }`.
 * Card-derived commands carry an envelope on `meta.cardBuilt`; plain slash
 * commands derive the envelope from the command's `intentType`.
 */
export function buildIntent(
  command: GameSlashCommand,
  selections: StepSelection[]
): { text: string; built: BuiltIntent | null } {
  const text = buildText(command, selections);
  const acc = accumulateSelections(selections);

  // Card-derived commands carry a pre-built BuiltIntent envelope on
  // `meta.cardBuilt` populated by `buildCardSlashCommand`. The selection layer
  // only fills runtime fields (picked spell + target for metamagic, target id
  // for use_ability with an enemy step).
  if (command.meta?.cardBuilt) {
    const cardBuilt = command.meta.cardBuilt as BuiltIntent;
    const mergedIntent = mergeCardIntent(cardBuilt.intent, acc);
    return {
      text,
      built: {
        intent: mergedIntent,
        action_slot: cardBuilt.action_slot,
        turn_flags: cardBuilt.turn_flags,
      },
    };
  }

  return { text, built: buildPlainIntent(command, acc, selections) };
}

// ============================================================
// Standalone Intent constructors (used by the structured-ability
// shortcuts in `useGameSlashCommands.ts` so all Intent construction lives
// in one file).
// ============================================================

/** Toggle ability click — emits `Intent::UseAbility` with `activate: true`.
 *  Caller supplies the slot (toggle abilities span action / bonus_action). */
export function buildToggleAbilityIntent(abilityName: string): Intent {
  return {
    intent_type: 'use_ability',
    ability_name: abilityName,
    target_ids: [],
    activate: true,
  };
}

/** Mode-selectable ability (Cunning Action / Step of the Wind / Patient
 *  Defense) — emits `Intent::UseAbility` with the chosen `mode_id`. */
export function buildModeAbilityIntent(abilityName: string, modeId: string): Intent {
  return {
    intent_type: 'use_ability',
    ability_name: abilityName,
    target_ids: [],
    mode_id: modeId,
  };
}

/** Self-only `Intent::UseAbility` — for shortcut paths (Second Wind, Action
 *  Surge, Lay on Hands typed as self-only). */
export function buildSelfUseAbilityIntent(abilityName: string): Intent {
  return {
    intent_type: 'use_ability',
    ability_name: abilityName,
    target_ids: [],
  };
}

/** Self-only `Intent::UseItem` — items with no target picker. */
export function buildSelfUseItemIntent(itemName: string, itemId?: string): Intent {
  const item: UsableItem = {
    item_type: 'magic_item',
    item_name: itemName,
    charges_used: 1,
    ...(itemId ? { item_id: itemId } : {}),
  };
  return { intent_type: 'use_item', item };
}

// Re-export for callers that want the same parser tools that resolveFromSubmit
// uses.
export type { CompletionStepType } from '@/components/panels/game-commands';

// ============================================================
// Type guards / re-exports for free-text branch builders
// ============================================================

/**
 * Map a raw user message to a typed `IntentSay` / `IntentShout` / `IntentEmote`
 * / `IntentTalkTo` / `IntentWhisper` for `/say`, `/shout`, `/sayto`, `/whisper`
 * commands when the user types a body. Returns `null` when the body is empty
 * or the intent isn't a communication variant.
 *
 * The hook chooses the right constructor based on `command.intentType` so the
 * body lands in `message` for say/shout/talk_to/whisper, `gesture` for emote.
 */
export function buildCommunicationIntent(
  intentType: string,
  body: string,
  targetId?: string
): Intent | null {
  const trimmed = body.trim();
  if (!trimmed) return null;
  switch (intentType) {
    case 'say': {
      const intent: IntentSay = { intent_type: 'say', message: trimmed };
      return intent;
    }
    case 'shout': {
      const intent: IntentShout = { intent_type: 'shout', message: trimmed };
      return intent;
    }
    case 'emote': {
      const intent: IntentEmote = { intent_type: 'emote', gesture: trimmed };
      return intent;
    }
    case 'talk_to': {
      if (!targetId) return null;
      const intent: IntentTalkTo = {
        intent_type: 'talk_to',
        target_id: targetId,
        message: trimmed,
      };
      return intent;
    }
    case 'whisper': {
      if (!targetId) return null;
      const intent: IntentWhisper = {
        intent_type: 'whisper',
        target_id: targetId,
        message: trimmed,
      };
      return intent;
    }
    default:
      return null;
  }
}

/** Build `IntentRequestCheck` from /check + skill picker (future use). */
export function buildRequestCheckIntent(skill: Skill, description: string): Intent {
  const intent: IntentRequestCheck = {
    intent_type: 'request_check',
    skill,
    description,
  };
  return intent;
}

/** Build `IntentMove { destination }` from /move + free-text destination. */
export function buildMoveIntent(destination: string): Intent {
  const trimmed = destination.trim();
  if (!trimmed) {
    // Caller guards against empty input; defensively emit empty so the
    // backend's resolver still has something to work with rather than
    // returning an invalid Intent.
    const intent: IntentMove = { intent_type: 'move', destination: '' };
    return intent;
  }
  const intent: IntentMove = { intent_type: 'move', destination: trimmed };
  return intent;
}

/** Build `IntentGiveCoins` from /givecoins + amount + target. */
export function buildGiveCoinsIntent(amountCp: number, targetId: string): Intent {
  const intent: IntentGiveCoins = {
    intent_type: 'give_coins',
    amount_cp: amountCp,
    target_id: targetId,
  };
  return intent;
}

/** Parse "5 gp" / "50 sp" / "100 cp" / "5 gold" / "10 silver" / "2 copper"
 *  → copper-piece total. Returns null when no recognised amount is found.
 *
 *  SRD 5.2 conversion (mirrors the enricher prompt's currency notes):
 *    1 gp = 100 cp, 1 sp = 10 cp, 1 cp = 1 cp.
 *
 *  Used by the slash menu's `/givecoins` finalizer so the FE ships
 *  `Intent::GiveCoins { amount_cp, target_id }` directly — the picker
 *  already supplied the target, the body supplies the amount, and the
 *  enricher LLM does not need to re-parse the prose. */
export function parseCoinAmount(body: string): number | null {
  const trimmed = body.trim().toLowerCase();
  if (!trimmed) return null;
  const match = trimmed.match(/^(\d+)\s*(gp|gold|g|sp|silver|s|cp|copper|c)\b/i);
  if (!match) return null;
  const n = Number.parseInt(match[1], 10);
  // Backend drops amount_cp <= 0 silently (no player warning). Reject zero
  // here so /givecoins doesn't ship a typed intent that's a guaranteed no-op.
  // Mobile already rejects <= 0 at the same gate.
  if (!Number.isFinite(n) || n <= 0) return null;
  const unit = match[2].toLowerCase();
  if (unit === 'gp' || unit === 'gold' || unit === 'g') return n * 100;
  if (unit === 'sp' || unit === 'silver' || unit === 's') return n * 10;
  return n;
}

/** Build `IntentUnequip` from /unequip + slot picker (future use). */
export function buildUnequipIntent(slot: string, itemId?: string): Intent {
  const intent: IntentUnequip = {
    intent_type: 'unequip',
    // Cast through unknown — `slot` is a runtime string until a slot picker exists.
    slot: slot as IntentUnequip['slot'],
    item_id: itemId,
  };
  return intent;
}
